<script setup lang="ts">
import { inject, ref, watch } from 'vue'
import { RrUpload, type UploadFile, type UploadRequestOptions } from '@xjlv2/ui'
import { DECORATION_UPLOAD_IMAGE_KEY, MAX_DECORATION_UPLOAD_BYTES, uploadSaasStorageImage } from './storage-upload'

const props = withDefaults(defineProps<{ modelValue?: string; disabled?: boolean }>(), { modelValue: '', disabled: undefined })
const emit = defineEmits<{ 'update:modelValue': [url: string] }>()
const injectedUpload = inject(DECORATION_UPLOAD_IMAGE_KEY, null)
const fileList = ref<UploadFile[]>([])

watch(() => props.modelValue.trim(), url => {
  if (fileList.value.some(file => file.status === 'done' && file.url === url)) return
  fileList.value = url ? [{ uid: url, name: 'image', url, status: 'done' }] : []
}, { immediate: true })

function customRequest(options: UploadRequestOptions) {
  const controller = new AbortController()
  const file = options.file instanceof File ? options.file : new File([options.file], 'image', { type: options.file.type })
  const upload = injectedUpload ?? uploadSaasStorageImage
  void (async () => {
    try {
      if (file.size > MAX_DECORATION_UPLOAD_BYTES) throw new Error('图片大小不能超过 10MB')
      const url = await upload(file, { signal: controller.signal })
      if (controller.signal.aborted) return
      if (!url || url.startsWith('blob:') || url.startsWith('data:')) throw new Error('上传成功但未返回图片地址')
      options.onSuccess({ url })
    } catch (cause) {
      if (!controller.signal.aborted) options.onError(cause instanceof Error ? cause : new Error(String(cause)))
    }
  })()
  return { abort: () => controller.abort() }
}

function onFileList(files: UploadFile[]) {
  // This adapter owns the { url } response contract; the generic Upload does not assume a server response shape.
  fileList.value = files.map(file => file.status === 'done' && file.response
    ? { ...file, url: (file.response as { url: string }).url }
    : file)
  if (!files.length) emit('update:modelValue', '')
  else {
    const done = fileList.value.find(file => file.status === 'done')
    if (done?.url) emit('update:modelValue', done.url)
  }
}
</script>

<template>
  <RrUpload list-type="picture-card" accept="image/*" :max-count="1" :disabled="disabled"
    :file-list="fileList" :custom-request="customRequest" @update:file-list="onFileList" />
</template>
